import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Dumbbell,
  MapPin,
  Phone,
  Mail,
  Sun,
  Moon,
  ChevronUp,
} from "lucide-react";

export default function NitrixLanding() {
  const [darkMode, setDarkMode] = useState(true);
  const [navSolid, setNavSolid] = useState(false);
  const { scrollYProgress } = useScroll();
  const [showTop, setShowTop] = useState(false);

  const heroParallax = useTransform(scrollYProgress, [0, 400], [0, 150]);

  useEffect(() => {
    const handleScroll = () => {
      setNavSolid(window.scrollY > 80);
      setShowTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div
      className={`flex flex-col min-h-screen transition-colors duration-500 ${
        darkMode ? "bg-[#2C3E50] text-white" : "bg-white text-[#2C3E50]"
      }`}
    >
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-[#ABC30D] z-[999] origin-left z-50"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Navbar */}
      <header
        className={`fixed w-full top-0 z-50 transition-all duration-500 ${
          navSolid
            ? darkMode
              ? "bg-[#1A252F] shadow-md"
              : "bg-white shadow-md"
            : "bg-transparent"
        } bg-opacity-90 backdrop-blur-md flex justify-between items-center px-6 py-4`}
      >
        <h1 className="text-2xl font-bold text-[#ABC30D]">Nitrix</h1>
        <nav className="space-x-6 hidden md:flex">
          {[
            { label: "Home", id: "hero" },
            { label: "About", id: "about" },
            { label: "Products", id: "products" },
            { label: "Why Nitrix?", id: "highlights" },
            { label: "Testimonials", id: "testimonials" },
            { label: "Contact", id: "contact" },
          ].map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className="hover:text-[#ABC30D] transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Sun className="w-4 h-4 text-[#ABC30D]" />
          <Switch
            checked={darkMode}
            onCheckedChange={setDarkMode}
            className="data-[state=checked]:bg-[#ABC30D]"
          />
          <Moon className="w-4 h-4 text-[#ABC30D]" />
        </div>
      </header>

      {/* Hero Section with Parallax */}
      <section
        id="hero"
        className="relative h-screen flex flex-col justify-center items-center text-center overflow-hidden"
      >
        <motion.div
          style={{
            y: heroParallax,
            backgroundImage:
              "url('https://images.pexels.com/photos/5327455/pexels-photo-5327455.jpeg')",
          }}
          className="absolute inset-0 bg-cover bg-center"
        ></motion.div>
        <div className="absolute inset-0 bg-black/60" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 max-w-3xl px-4 text-white"
        >
          <img
            src="/nitrix.svg"
            alt="Nitrix Logo"
            className="mx-auto mb-6 w-100 h-50"
          />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-[#ABC30D]">
            Nitrix & Jhaojun Sun Fitness
          </h1>
          <p className="text-lg md:text-xl mb-8">
            Empowering gyms and fitness enthusiasts across Bangladesh with
            world-class fitness equipment.
          </p>
          <a href="#products">
            <Button className="bg-[#ABC30D] text-[#2C3E50] font-bold text-lg px-8 py-6 rounded-2xl hover:bg-[#a0b80d]">
              Explore Products
            </Button>
          </a>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-6 text-[#ABC30D]">About Us</h2>
          <p className="text-lg leading-relaxed">
            <strong>Nitrix</strong> is a trusted gym equipment supplier based in
            Chattogram, Bangladesh. We specialize in providing high &amp; top
            quality gym equipment and fitness gear designed for both commercial
            gyms and personal training studios. Our mission is to bring durable,
            high-performance and stylish gym equipment to empower the growing
            fitness community of Bangladesh. We proudly support professional
            gyms, fitness centers, and home workout enthusiasts across
            Bangladesh.
          </p>
        </motion.div>
      </section>

      {/* Product Showcase */}
      <section id="products" className="py-20 bg-[#F8FAFC] text-center px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#2C3E50] mb-12">
            Product Showcase
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {["LAT-PULL-DOWN", "HACK-SQUAT", "BICEP-CURL"].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="shadow-lg border-none hover:shadow-xl transition-all overflow-hidden">
                  <img
                    src={`${i}.jpg`}
                    alt={`${i}`}
                    className="w-full h-48 object-cover"
                  />
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-[#2C3E50] mb-2">
                      {i}
                    </h3>
                    <p className="text-gray-600">
                      Premium fitness equipment for your ultimate training
                      experience.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section id="highlights" className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#ABC30D] mb-12">
            Why Choose Nitrix?
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {["Durability", "Performance", "Innovation"].map((feature) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="shadow-lg border-none hover:shadow-xl transition-all text-center p-8">
                  <Dumbbell className="w-12 h-12 text-[#ABC30D] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature}</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Our {feature.toLowerCase()} ensures you get the best value
                    for your fitness investment.
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-[#F8FAFC] px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#2C3E50] mb-12">
            What Our Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Rafi Ahmed",
                text: "Nitrix provided excellent gym setups for our commercial fitness center — top-notch quality!",
              },
              {
                name: "Rahim Uddin",
                text: "Nitrix supplied our entire gym setup at an excellent price. Their equipment quality and after-sales support are top-notch.",
              },
              {
                name: "FitZone Gym",
                text: "We've been using Nitrix Fitness machines for over a year — very durable and professional-grade build.",
              },
              {
                name: "Sarah Islam",
                text: "Love their dumbbells and treadmills! Sturdy and stylish, perfect for my home gym.",
              },
              {
                name: "Tanvir Rahman",
                text: "Customer service is fantastic. They really care about your fitness goals!",
              },              
            ].map((t) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="shadow-md p-6 border-none hover:shadow-xl">
                  <CardContent>
                    <p className="italic text-gray-700 mb-4">“{t.text}”</p>
                    <h4 className="font-bold text-[#ABC30D]">- {t.name}</h4>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 w-full">
        <iframe
          title="Nitrix Location"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.025574284163!2d91.80743737522377!3d22.387126841104383!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30acd89786f69e11%3A0x9a8c3a46c960d0c8!2sAgrabad%20Access%20Rd%2C%20Chattogram!5e0!3m2!1sen!2sbd!4v1709399999999"
        ></iframe>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-[#ABC30D]">
              Contact Us
            </h2>
            <p className="mb-6">
              Have questions or want to place an order? Reach out — we’d love to
              help you find the perfect equipment.
            </p>
            <div className="space-y-3">
              <p className="flex items-center gap-3">
                <MapPin className="text-[#ABC30D]" /> 
                AN Bulilding, <br/> 104, Stand Road, Port Container, Terminal Gate-2, Port, <br/>Goshaildanga, Chattogram, Bangladesh.
              </p>
              <p className="flex items-center gap-3">
                <Phone className="text-[#ABC30D]" /> +880 1866-325560
              </p>
              <p className="flex items-center gap-3">
                <Mail className="text-[#ABC30D]" /> info@nitrixfitness.com
              </p>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className={`p-8 rounded-2xl shadow-xl space-y-4 ${
              darkMode ? "bg-[#1A252F] text-white" : "bg-white"
            }`}
          >
            <Input placeholder="Your Name" required />
            <Input type="email" placeholder="Your Email" required />
            <Textarea placeholder="Your Message" rows={4} required />
            <Button className="w-full bg-[#ABC30D] text-[#2C3E50] hover:bg-[#a0b80d] font-bold">
              Send Message
            </Button>
          </motion.form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-[#1A252F] text-center text-gray-400 text-sm">
        <p>
          © {new Date().getFullYear()} Nitrix & Jhaojun Sun Fitness. All rights
          reserved.
        </p>
        <p>
          Developed by <span className="text-[#ABC30D]"><a href="https://syncmachine.com/" target="_blank" rel="noopener noreferrer">syncmachine, inc</a></span>.
        </p>
      </footer>

      {/* Back to Top Button */}
      {showTop && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-[#ABC30D] text-[#2C3E50] p-3 rounded-full shadow-lg hover:bg-[#a0b80d] transition"
        >
          <ChevronUp size={24} />
        </motion.button>
      )}
    </div>
  );
}
